import apiClient, { extractApiPayloadMessage, getApiErrorMessage } from './apiClient';
import { replaceEntityFieldsApi } from './entitiesAdmin.api';

const assertAdminSuccess = (payload, fallbackMessage) => {
  if (payload && typeof payload === 'object' && payload.success === false) {
    throw new Error(extractApiPayloadMessage(payload) || fallbackMessage);
  }
};

const normalizeForms = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.items)) {
    return payload.items;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  return [];
};

export const fetchProjectFormsApi = async (projectId) => {
  try {
    const { data } = await apiClient.get(`/projects/${encodeURIComponent(projectId)}/forms`);
    assertAdminSuccess(data, 'Failed to load project forms.');
    return normalizeForms(data);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load project forms.'));
  }
};

export const fetchFormSubmissionsApi = async ({ projectId, formId, page = 1, pageSize = 20 }) => {
  try {
    const { data } = await apiClient.get(
      `/projects/${encodeURIComponent(projectId)}/forms/${encodeURIComponent(formId)}/submissions`,
      {
        params: { page, pageSize }
      }
    );
    assertAdminSuccess(data, 'Failed to load form submissions.');

    return {
      items: Array.isArray(data?.items) ? data.items : [],
      page: typeof data?.page === 'number' ? data.page : page,
      pageSize: typeof data?.pageSize === 'number' ? data.pageSize : pageSize,
      totalCount: typeof data?.totalCount === 'number' ? data.totalCount : 0
    };
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load form submissions.'));
  }
};

export const createFormSubmissionApi = async ({ projectId, formId, jsonData }) => {
  try {
    const { data } = await apiClient.post(
      `/projects/${encodeURIComponent(projectId)}/forms/${encodeURIComponent(formId)}/submissions`,
      { jsonData }
    );
    assertAdminSuccess(data, 'Failed to create submission.');

    return data;
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to create submission.'));
  }
};

export const updateFormSubmissionApi = async ({ projectId, formId, submissionId, jsonData }) => {
  try {
    const { data } = await apiClient.put(
      `/projects/${encodeURIComponent(projectId)}/forms/${encodeURIComponent(formId)}/submissions/${encodeURIComponent(submissionId)}`,
      { jsonData }
    );
    assertAdminSuccess(data, 'Failed to update submission.');

    return data;
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to update submission.'));
  }
};

export const deleteFormSubmissionApi = async ({ projectId, formId, submissionId }) => {
  try {
    const { data } = await apiClient.delete(
      `/projects/${encodeURIComponent(projectId)}/forms/${encodeURIComponent(formId)}/submissions/${encodeURIComponent(submissionId)}`
    );
    assertAdminSuccess(data, 'Failed to delete submission.');

    return data;
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to delete submission.'));
  }
};

export const updateProjectFormApi = async ({ projectId, formId, displayName, authNeed, isPrivate }) => {
  try {
    const { data } = await apiClient.put(
      `/projects/${encodeURIComponent(projectId)}/forms/${encodeURIComponent(formId)}`,
      {
        DisplayName: displayName,
        AuthNeed: authNeed,
        IsPrivate: isPrivate
      }
    );
    assertAdminSuccess(data, 'Failed to update form.');

    return data;
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to update form.'));
  }
};

export const saveProjectFormWithSchemaApi = async ({
  projectId,
  formId,
  slug,
  displayName,
  authNeed,
  isPrivate,
  fields
}) => {
  const updated = await updateProjectFormApi({
    projectId,
    formId,
    displayName,
    authNeed,
    isPrivate
  });

  if (slug && Array.isArray(fields) && fields.length) {
    await replaceEntityFieldsApi({
      projectId,
      entity: slug,
      fields
    });
  }

  return updated;
};

export const deleteProjectFormApi = async ({ projectId, formId }) => {
  try {
    const { data } = await apiClient.delete(
      `/projects/${encodeURIComponent(projectId)}/forms/${encodeURIComponent(formId)}`
    );
    assertAdminSuccess(data, 'Failed to delete form.');

    return data;
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to delete form.'));
  }
};
